/**
 * Email verification status for the logged-in user.
 * Used by the global verify-account banner (alerts require a verified email).
 */

export var EMAIL_ADDRESSES_URL = '/api/identity/email-addresses'
export var EMAIL_VERIFICATION_RESEND_URL = '/api/identity/verify-email/resend'
export var IDENTITY_SETTINGS_URL = '/api/identity/settings'
export var ACCOUNT_SETTINGS_PATH = '/settings/account'

function truthyFlag (value) {
  return value === true || value === 'true' || value === 1 || value === '1'
}

function falsyFlag (value) {
  return value === false || value === 'false' || value === 0 || value === '0'
}

/** Normalize verified flag; null if the API omitted it. */
export function parseVerifiedFlag (value) {
  if (value === undefined || value === null || value === '') return null
  if (truthyFlag(value)) return true
  if (falsyFlag(value)) return false
  if (typeof value === 'string') {
    var s = value.trim().toLowerCase()
    if (s === 'verified' || s === 'yes') return true
    if (s === 'unverified' || s === 'no' || s === 'pending') return false
  }
  return null
}

function normalizeEmail (email) {
  return email != null ? String(email).trim().toLowerCase() : ''
}

function pickId (row) {
  if (!row || typeof row !== 'object') return null
  var id =
    row.emailAddressId != null ? row.emailAddressId
      : row.email_address_id != null ? row.email_address_id
        : row.userAccountId != null ? row.userAccountId
          : row.user_account_id != null ? row.user_account_id
            : row.id != null ? row.id
              : null
  if (id != null && typeof id === 'object' && id.value !== undefined) id = id.value
  if (id == null || id === '') return null
  return id
}

function rowEmail (row) {
  if (!row) return ''
  return normalizeEmail(row.email != null ? row.email : row.emailAddress)
}

function rowIsVerified (row) {
  if (!row) return null
  var v = parseVerifiedFlag(row.isVerified)
  if (v !== null) return v
  v = parseVerifiedFlag(row.is_verified)
  if (v !== null) return v
  v = parseVerifiedFlag(row.emailVerified)
  if (v !== null) return v
  v = parseVerifiedFlag(row.email_verified)
  if (v !== null) return v
  v = parseVerifiedFlag(row.verified)
  if (v !== null) return v
  if (row.status != null) {
    v = parseVerifiedFlag(row.status)
    if (v !== null) return v
  }
  return null
}

/** Pull email-address rows from flexible API envelopes. */
export function emailAddressListFromResponseBody (body) {
  if (!body) return []
  if (Array.isArray(body)) return body
  if (Array.isArray(body.emailAddresses)) return body.emailAddresses
  if (Array.isArray(body.email_addresses)) return body.email_addresses
  if (Array.isArray(body.emails)) return body.emails
  if (Array.isArray(body.data)) return body.data
  if (body.data && Array.isArray(body.data.emailAddresses)) return body.data.emailAddresses
  if (body.data && Array.isArray(body.data.email_addresses)) return body.data.email_addresses
  return []
}

/**
 * From GET /api/identity/email-addresses + login email.
 * Only matches the login email (or primary when login unknown).
 * Returns null when status cannot be determined for that user.
 */
export function verificationStatusFromEmailAddresses (list, loginEmail) {
  if (!Array.isArray(list) || list.length === 0) return null
  var want = normalizeEmail(loginEmail)
  var match = null
  if (want) {
    for (var i = 0; i < list.length; i++) {
      if (rowEmail(list[i]) === want) {
        match = list[i]
        break
      }
    }
    // Do not fall back to another tenant email — that falsely shows the banner
    // for verified users when a secondary address is unverified.
    if (!match) return null
  } else {
    match = list.filter(function (r) {
      return r && (r.isPrimary === true || r.is_primary === true)
    })[0] || list[0]
  }
  if (!match) return null
  var verified = rowIsVerified(match)
  if (verified === null) return null
  return {
    emailVerified: verified,
    email: rowEmail(match) || want,
    emailAddressId: pickId(match)
  }
}

/**
 * From GET /api/identity/settings body + login email.
 * Prefer matching user_account row; fall back to body-level flags.
 */
export function verificationStatusFromIdentitySettings (body, loginEmail) {
  if (!body || typeof body !== 'object') return null
  var want = normalizeEmail(loginEmail || body.email)
  var accounts = Array.isArray(body.user_account)
    ? body.user_account
    : (Array.isArray(body.userAccount) ? body.userAccount : [])
  var match = null
  if (want && accounts.length) {
    for (var i = 0; i < accounts.length; i++) {
      if (rowEmail(accounts[i]) === want) {
        match = accounts[i]
        break
      }
    }
  }
  if (!match && !want && accounts.length) {
    match = accounts.filter(function (ua) {
      return ua && (ua.isPrimary === true || ua.is_primary === true)
    })[0] || accounts[0]
  }
  if (match) {
    var verified = rowIsVerified(match)
    if (verified === null) {
      // Legacy rows omit the flag — treat as verified so the banner stays hidden.
      verified = true
    }
    return {
      emailVerified: verified,
      email: rowEmail(match) || want,
      emailAddressId: pickId(match)
    }
  }
  var bodyVerified = parseVerifiedFlag(body.emailVerified)
  if (bodyVerified === null) bodyVerified = parseVerifiedFlag(body.email_verified)
  if (bodyVerified === null) bodyVerified = parseVerifiedFlag(body.isVerified)
  if (bodyVerified === null) bodyVerified = parseVerifiedFlag(body.is_verified)
  if (bodyVerified === null) return null
  return {
    emailVerified: bodyVerified,
    email: want || normalizeEmail(body.email),
    emailAddressId: pickId(body)
  }
}

/**
 * From /api/auth/isauthenticated. Only returns a status when the flag is present.
 * Callers should still confirm via identity APIs (auth alone can be stale/wrong).
 */
export function verificationStatusFromAuthBody (body) {
  if (!body || typeof body !== 'object') return null
  var verified = parseVerifiedFlag(body.emailVerified)
  if (verified === null) verified = parseVerifiedFlag(body.email_verified)
  if (verified === null) verified = parseVerifiedFlag(body.isVerified)
  if (verified === null) verified = parseVerifiedFlag(body.is_verified)
  if (verified === null) verified = parseVerifiedFlag(body.verified)
  if (verified === null) return null
  return {
    emailVerified: verified,
    email: normalizeEmail(body.email),
    emailAddressId: pickId(body)
  }
}

export function verifyEmailStripContent (opts) {
  opts = opts || {}
  var email = opts.email ? String(opts.email) : ''
  var sent = !!opts.verificationEmailSent
  var sending = !!opts.verificationEmailSending
  var messageHtml = sent
    ? 'Verification email sent' + (email ? ' to <strong>' + escapeHtml(email) + '</strong>' : '') + '. Check your inbox — email alerts stay off until you verify.'
    : 'Verify your account' + (email ? ' (<strong>' + escapeHtml(email) + '</strong>)' : '') +
      ' — otherwise email alerts won\u2019t trigger for you.'
  return {
    tone: 'orange',
    messageHtml: messageHtml,
    ctaLabel: sending ? 'Sending\u2026' : (sent ? 'Resend again' : 'Resend verification'),
    ctaAction: 'resend-verification',
    dismissible: false
  }
}

function escapeHtml (s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}



// WEBPACK FOOTER //
// ./src/utils/emailVerification.js